import { useCallback, useRef, useState } from 'react';
import { setZendeskWidgetVisible } from '@/components/atoms/zendesk-widget/set-visibility';
import { useScrollbarWidth } from '../useScrollbarWidth';
import useTouchDevice from '../useTouchDevice';

// Scroll-blocking is coordinated across every consumer via a module-level
// ref-count. More than one panel can be open or handed off at once (e.g. the
// mobile nav Drawer opening the Products slide-menu), so the body must stay
// locked until the LAST blocker closes — one panel closing must not unlock
// scroll while another is still open.
//
// We capture the body/header inline styles on the FIRST block and restore
// exactly those on the LAST unblock, rather than hard-resetting to
// 'auto'/'0px'. A page that intentionally reserves a scrollbar gutter
// (`overflow-y: scroll`) would otherwise lose it on every close, causing a
// layout shift.
let activeBlockers = 0;
let savedStyles: { bodyOverflowY: string; bodyPaddingRight: string; headerPaddingRight: string } | null = null;

const useScrollBlock = () => {
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const { isTouchDevice } = useTouchDevice();
  const scrollbarWidth = useScrollbarWidth();
  // Each hook instance contributes at most 1 to the global count, so repeated
  // calls with the same status (e.g. a parent re-rendering, or an effect that
  // re-fires) are no-ops and can't double-count or drive the count negative.
  const contributesRef = useRef(false);

  const blockScroll = useCallback(
    (status: boolean) => {
      if (status === contributesRef.current) return;
      contributesRef.current = status;

      const header = document.querySelector('#header-container') as HTMLDivElement | null;

      if (status) {
        if (activeBlockers === 0) {
          savedStyles = {
            bodyOverflowY: document.body.style.overflowY,
            bodyPaddingRight: document.body.style.paddingRight,
            headerPaddingRight: header?.style.paddingRight ?? '',
          };

          if (!isTouchDevice) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            if (header) header.style.paddingRight = `${scrollbarWidth}px`;
          }

          document.body.style.overflowY = 'hidden';
          // EWM-659 — hide the Zendesk chat launcher while any panel is open.
          // Its fixed launcher out-z-indexes our drawers and overlaps their
          // footer controls (e.g. the All Filters "Clear Filters" button). We
          // drive Zendesk's own messenger API (via `setZendeskWidgetVisible`,
          // the EWM-630 mechanism) rather than a CSS selector on the injected
          // iframe, which is brittle against widget DOM changes. Hidden on the
          // FIRST blocker, restored on the LAST, so it tracks any-panel-open
          // exactly like the scroll lock.
          setZendeskWidgetVisible(false);
        }

        activeBlockers += 1;
        setIsBlocked(true);
      } else {
        activeBlockers -= 1;

        if (activeBlockers === 0 && savedStyles) {
          document.body.style.overflowY = savedStyles.bodyOverflowY;
          document.body.style.paddingRight = savedStyles.bodyPaddingRight;
          if (header) header.style.paddingRight = savedStyles.headerPaddingRight;
          savedStyles = null;
          // EWM-659 — last panel closed: let the Zendesk launcher return.
          setZendeskWidgetVisible(true);
        }

        setIsBlocked(false);
      }
    },
    [isTouchDevice, scrollbarWidth],
  );

  return { isBlocked, blockScroll };
};

export default useScrollBlock;
